// Centralized Czech copy for Hudba (Modul 6): the state of a přihláška's
// recording and the verdicts analysis reaches about it. Mirrors
// memberState.ts / startListState.ts — see the terminology table in
// AGENTS.md and do not paraphrase these strings.
//
// MUSIC_WARNING_LABELS is a CONTRACT with internal/service/music: the
// warning kinds are machine keys emitted by Go, and a kind that arrives
// without a case here renders as nothing at all — no type error at
// runtime, no failing test. e2e/specs/hudba/contract.spec.ts compares the
// two sides so that cannot happen quietly.
import { formatDuration as libFormatDuration, pluralizeCzech } from "@marekl/adminer/format";
import type { MusicSource, MusicWarning, MusicWarningKind } from "./queries/music";
import type { Entry } from "./queries/entries";

type BadgeVariant = "ok" | "warn" | "err" | "info" | "muted";

export interface StateBadge {
  label: string;
  variant: BadgeVariant;
}

/** What a přihláška's music column says. */
export type MusicState = "uploaded" | "missing" | "federation" | "none";

// `none` (improvizace) reads „Hudba ČNSMS" like `federation` does. The two
// are different sources and the states stay distinct — the organizer's
// list names them apart, see ORGANIZER_BADGES — but to a club they mean
// the same thing: the federation supplies the track, you owe nothing.
export const MUSIC_STATE_LABELS: Record<MusicState, string> = {
  uploaded: "Nahráno",
  missing: "Chybí hudba",
  federation: "Hudba ČNSMS",
  none: "Hudba ČNSMS",
};

export const MUSIC_STATE_VARIANT: Record<MusicState, BadgeVariant> = {
  uploaded: "ok",
  missing: "warn",
  federation: "info",
  // Same colour as `federation`, because it is now the same words. Two
  // badges reading „Hudba ČNSMS" in two colours in one column reads as a
  // bug rather than as a distinction.
  none: "info",
};

/**
 * musicState resolves what an entry's recording state is.
 *
 * Only `own` can be "missing". A federation entry is danced to the
 * official track and a `none` entry (improvizace) to whatever the zvukař
 * puts on — neither owes a file, and the switch is exhaustive rather
 * than a chain of negatives so a fourth source cannot silently fall
 * through to "chybí hudba".
 */
export function musicState(source: MusicSource, hasFile: boolean): MusicState {
  switch (source) {
    case "federation":
      return "federation";
    case "none":
      return "none";
    case "own":
      return hasFile ? "uploaded" : "missing";
  }
}

/**
 * How many of a team's přihlášky still owe a file: submitted ones whose
 * musicState is `missing`. An odhláška owes nothing, the same exclusion
 * the zvukař's export makes.
 */
export function musicMissingCount(
  entries: Pick<Entry, "status" | "music_source" | "music">[],
): number {
  return entries.filter(
    (e) => e.status === "submitted" && musicState(e.music_source, e.music !== null) === "missing",
  ).length;
}

/**
 * What the ORGANIZER reads where the club reads „Hudba ČNSMS".
 *
 * The club is told the same thing for `federation` and `none`, and that
 * is deliberate: to a club both mean „hudbu dodá federace, nic
 * nenahrávejte". „Zajišťuje zvukař" names an internal arrangement they
 * cannot act on.
 *
 * The organizer is the person that arrangement is FOR — a start with no
 * file anywhere is one the zvukař has to be briefed about — so their
 * list keeps the two apart. `muted` against `federation`'s `info`:
 * `none` owes nothing and blocks nothing, so it must not borrow `warn`
 * or `err`, which mean a file is missing or bad.
 */
const ORGANIZER_BADGES: Partial<Record<MusicState, StateBadge>> = {
  none: { label: "Zajišťuje zvukař", variant: "muted" },
};

export function musicBadge(
  source: MusicSource,
  hasFile: boolean,
  audience: MusicAudience = "team",
): StateBadge {
  const state = musicState(source, hasFile);
  if (audience === "organizer") {
    const override = ORGANIZER_BADGES[state];
    if (override) return override;
  }
  return { label: MUSIC_STATE_LABELS[state], variant: MUSIC_STATE_VARIANT[state] };
}

/** The short label; the sentence a leader acts on comes from
 * warningMessage, which knows the numbers.
 *
 * Two kinds deliberately share one label: bitrate and vzorkovací
 * frekvence are the same finding to the person who has to fix it — the
 * file is not good enough — and naming the measurement told them
 * nothing they could act on. Render through musicWarningItems, never by
 * mapping this record directly, or a file that trips both says it
 * twice. */
export const MUSIC_WARNING_LABELS: Record<MusicWarningKind, string> = {
  duration_over: "Delší než limit",
  duration_under: "Kratší než limit",
  low_bitrate: "Špatná kvalita nahrávky",
  low_sample_rate: "Špatná kvalita nahrávky",
};

/**
 * The only two severities a music warning can have. Narrower than
 * `BadgeVariant` on purpose, and that narrowness is load-bearing: it is
 * what lets one value go into both a `Badge` and an `Alert`, whose union
 * has no `muted`.
 */
export type MusicWarningVariant = "warn" | "err";

/**
 * How severe each warning looks, in ONE place.
 *
 * It exists because the same finding used to be two colours: the badge
 * in the entry lists was hard-coded `err` and the alert in `MusicPanel`
 * hard-coded `warn`, so a leader met a red chip on the list and an
 * orange box on the detail for the identical file.
 *
 * The split is by what the finding costs. A duration outside the limit
 * breaks a rule and carries a deduction, whichever side it is out on: a
 * short routine is as much a fault as a long one, so both are `err`.
 * Poor quality breaks no rule; the track plays, the competition runs,
 * and it is advice about a master worth re-exporting.
 *
 * Read this through `musicWarningItems`, never directly, so a file that
 * trips both quality checks still says so once.
 */
export const MUSIC_WARNING_VARIANT: Record<MusicWarningKind, MusicWarningVariant> = {
  duration_over: "err",
  duration_under: "err",
  low_bitrate: "warn",
  low_sample_rate: "warn",
};

/** Who is reading. The distinction is not cosmetic — see warningMessage. */
export type MusicAudience = "team" | "organizer";

/**
 * The full sentence for one warning.
 *
 * Every deviation from the limit speaks up, at one severity — a leader
 * wants to know either way.
 *
 * The grace band is told to ORGANIZERS ONLY. It decides whether a
 * deduction is at stake, which is what an organizer fielding a question
 * about it needs; told to the club it reads as permission, and a leader
 * who hears „bez srážky" stops fixing a track that still does not match
 * the choreography the rules describe. The default audience is therefore
 * the team — a caller who forgets to say leaks nothing.
 */
export function warningMessage(w: MusicWarning, audience: MusicAudience = "team"): string {
  const tolerance =
    audience === "organizer" && w.within_tolerance ? " (v toleranci, bez srážky)" : "";
  const n = w.seconds ?? 0;
  const by = `${n} ${pluralizeCzech(n, ["sekundu", "sekundy", "sekund"])}`;
  switch (w.kind) {
    case "duration_over":
      return `Hudba je o ${by} delší, než dovoluje limit${tolerance}.`;
    case "duration_under":
      return `Hudba je o ${by} kratší, než dovoluje limit${tolerance}.`;
    // One sentence for both: they share a label, so two alerts would
    // otherwise stack the same heading over two paraphrases of it.
    case "low_bitrate":
    case "low_sample_rate":
      return "Nahrávka má nízkou kvalitu, může znít zkresleně.";
  }
}

/** One warning as it should be READ. */
export interface MusicWarningItem {
  kind: MusicWarningKind;
  label: string;
  message: string;
  /**
   * Severity, so every surface paints the same finding the same way.
   *
   * Narrower than `BadgeVariant` on purpose: a warning is never `ok`,
   * `info` or `muted`, and the narrow type is also what lets the same
   * value go straight into an `Alert`, whose union has no `muted`.
   */
  variant: MusicWarningVariant;
}

/**
 * musicWarningItems is THE way to render a recording's warnings: Czech
 * label and sentence, collapsed by label so a file that trips both
 * quality checks says so once instead of twice in a row.
 *
 * First occurrence wins and the server's order is preserved.
 */
export function musicWarningItems(
  warnings: MusicWarning[],
  audience: MusicAudience = "team",
): MusicWarningItem[] {
  const items: MusicWarningItem[] = [];
  for (const w of warnings) {
    const label = MUSIC_WARNING_LABELS[w.kind];
    if (items.some((i) => i.label === label)) continue;
    items.push({
      kind: w.kind,
      label,
      message: warningMessage(w, audience),
      variant: MUSIC_WARNING_VARIANT[w.kind],
    });
  }
  return items;
}

/** „3,4 MB" — the size a leader recognises from their own disk. */
export function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/**
 * „2:37" — a duration in the same shape as every time limit.
 *
 * Rounded half-up, and the rounding is NOT decorative: `music.Warnings`
 * (`internal/service/music/annotate.go`) judges the file by
 * `(DurationMs + 500) / 1000`, so a 2:45,9 file against a 2:45 limit is one
 * second over and says so. Truncating here would print „2:45" beside that
 * warning — the length shown equal to the limit the leader is being told they
 * exceeded, with no way to reconcile the two. Hence the explicit round rather
 * than `{ unit: "milliseconds" }`, which truncates.
 */
export function formatDuration(durationMs: number): string {
  return libFormatDuration(Math.round(durationMs / 1000), { format: "ms" });
}
